import { ref, createApp, h } from "vue";

interface NotifyOptions {
  text: string;
  variant?: "success" | "error" | "info" | "warning";
  size?: "small" | "medium" | "large";
  timeoutInMs?: number;
}

/**
 * Lightweight snackbar notification system.
 * Creates a temporary Vue app to render a Snackbar component.
 */
export default defineNuxtPlugin(() => {
  function notify(options: NotifyOptions) {
    if (!import.meta.client) return;

    const container = document.createElement("div");
    document.body.appendChild(container);

    const visible = ref(true);

    const SnackbarComponent = {
      setup() {
        const timeout = options.timeoutInMs ?? 3000;
        setTimeout(() => {
          visible.value = false;
          setTimeout(() => {
            app.unmount();
            container.remove();
          }, 300);
        }, timeout);

        return () =>
          visible.value
            ? h(
                "div",
                {
                  class: `snackbar snackbar--${options.variant || "info"} snackbar--${options.size || "medium"}`,
                  style: {
                    position: "fixed",
                    top: "20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    color: "#fff",
                    backgroundColor:
                      options.variant === "error"
                        ? "#e74c3c"
                        : options.variant === "success"
                          ? "#27ae60"
                          : options.variant === "warning"
                            ? "#f39c12"
                            : "#333",
                    zIndex: 9999,
                    transition: "opacity 0.3s",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    fontSize: "14px",
                  },
                },
                options.text,
              )
            : null;
      },
    };

    const app = createApp(SnackbarComponent);
    app.mount(container);
  }

  return { provide: { notify } };
});
